import { useState, Fragment, useEffect } from 'react';
import BTable from 'react-bootstrap/Table';
import {
    flexRender,
    functionalUpdate,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table';
import { Alert, ButtonToolbar, Col, Dropdown, DropdownButton, Row, Spinner } from 'react-bootstrap';
import SafTableSortIcons from './Sort/SafTableSortIcons';
import { SafTableSortDirection } from "./Sort/SafTableSortDirection";
import { ISafTableFilter } from './Filters/ISafTableFilter';
import { SafTableFilters } from './Filters/SafTableFilters';
import { SafTableSearch } from './Search/SafTableSearch';
import { SafTablePaginator } from './Paginate/SafTablePaginator';
import { ISafTablePaginationState } from './Paginate/ISafTablePaginationState';
import { ISafTableSortingState } from './Sort/ISafTableSortingState';
import { ISafTableProps } from './ISafTableProps';
import { useLocalStorage } from '../Hooks/useLocalStorage';


// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const SafTable = <T extends unknown>({
    tableKey,
    bordered,
    striped,
    responsive,
    hover,
    handleSearch,
    handleFilters,
    handleRowClick,
    handlePagination,
    handleSort,
    filters,
    isLoading,
    data,
    columns,
    initialSortingState,
    initialFilterState,
    totalSize,
    customNoDataMessage,
    errorMessage
}: ISafTableProps<T>): JSX.Element => {

    const [pageSize, setPageSize] = useLocalStorage<number>(tableKey + "-page-size", 10);
    const [currentFilters, setCurrentFilters] = useLocalStorage<ISafTableFilter[]>(tableKey + "-filters", initialFilterState ?? []);
    const [sorting, setSorting] = useState<ISafTableSortingState>(initialSortingState);
    const [fiterIsOpen, setFilterIsOpen] = useState<boolean>(false);
    const [pageIndex, setPageIndex] = useState<number>(0);
    const [pagination, setPagination] = useState<ISafTablePaginationState>({
        pageIndex,
        pageSize
    });

    useEffect(() => {
        setPagination({ pageIndex, pageSize });
    }, [pageIndex, pageSize]);


    const table = useReactTable({
        data,
        columns,
        pageCount: Math.ceil(totalSize / pageSize),
        state: {
            sorting,
            pagination
        },
        onSortingChange: (updater) => {
            const newValue = functionalUpdate(updater, sorting)
            handleSort?.(newValue)
            setSorting(newValue)
        },
        onPaginationChange: (updater) => {
            const newValue = functionalUpdate(updater, pagination)
            handlePagination?.(newValue)
            setPageSize(newValue.pageSize);
            setPageIndex(newValue.pageIndex);
        },
        manualSorting: handleSort !== undefined,
        manualPagination: handlePagination !== undefined,
        manualFiltering: handleFilters !== undefined,
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    const headerGroups = table.getHeaderGroups();

    const onFilters = (value: ISafTableFilter[]) => {
        setCurrentFilters(value);
        handleFilters?.(value);
    }

    const renderRows = () => {
        const colSpan = table.getHeaderGroups().reduce((a, b) => a + b.headers.length, 0);
        const rowModel = table.getRowModel();
        if (errorMessage) {
            return (
                <tr>
                    <td colSpan={colSpan}>
                        <Alert variant='danger' className='text-center'>
                            {errorMessage}
                        </Alert>
                    </td>
                </tr>
            );
        } else if (isLoading) {
            return (
                <tr>
                    <td colSpan={colSpan}>
                        <div className="text-center">
                            <Spinner animation="border" role="status" variant="primary">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                    </td>
                </tr>

            )
        } else if (rowModel.rows.length < 1) {
            return (
                <tr>
                    <td colSpan={colSpan}>
                        <Alert variant='warning' className='text-center'>
                            {customNoDataMessage ?? "no data"}
                        </Alert>
                    </td>
                </tr>

            )
        }
        return (
            rowModel.rows.map((row, index) => (

                <tr
                    key={index}
                    className={handleRowClick && 'clickable'}
                    onClick={() => {
                        handleRowClick && handleRowClick(row.original);
                    }}
                >
                    {row.getVisibleCells().map((cell, index) => (
                        <td key={index}>
                            {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                            )}
                        </td>
                    ))}
                </tr>
            ))
        )
    }


    return (

        <Fragment>
            <Row>
                {(handleSearch && filters) &&
                    <Fragment>
                        <Col md={9} lg={10} className="pb-2">
                            <SafTableSearch
                                isLoading={isLoading}
                                onChange={(value: string) => handleSearch?.(value)}
                            />
                        </Col>
                        <Col md={3} lg={2} className="pb-2">
                            <div className='float-end'>
                                <SafTableFilters
                                    isOpen={fiterIsOpen}
                                    handleOpen={setFilterIsOpen}
                                    filters={filters}
                                    initialFilterState={currentFilters}
                                    onFilters={onFilters}
                                />
                            </div>
                        </Col>
                    </Fragment>
                }
                {(handleSearch && !filters) &&
                    <Col md={12} className="pb-2">
                        <SafTableSearch
                            isLoading={isLoading}
                            onChange={(value: string) => handleSearch(value)}
                        />
                    </Col>
                }
                {(!handleSearch && filters) &&
                    <Col md={12} className="pb-2">
                        <div className='float-end'>
                            <SafTableFilters
                                isOpen={fiterIsOpen}
                                handleOpen={setFilterIsOpen}
                                filters={filters}
                                initialFilterState={currentFilters}
                                onFilters={onFilters}
                            />
                        </div>
                    </Col>
                }
            </Row>

            <div className='safTable'>
                <BTable bordered={bordered} striped={striped} hover={hover} responsive={responsive} style={{ overflow: 'visible' }}>
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header, index) => (
                                    <th key={index} colSpan={header.colSpan}>
                                        {header.isPlaceholder ? null : (
                                            <div className='d-flex flex-nowrap flex-row'>
                                                <Col md={10}>
                                                    <div className='justify-content-start me-1 text-nowrap'>
                                                        {
                                                            flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )
                                                        }
                                                    </div>
                                                </Col>
                                                <Col md={2}>
                                                    <div onClick={header.column.getToggleSortingHandler()}>
                                                        {
                                                            (
                                                                header.column.getCanSort() &&
                                                                {
                                                                    asc: SafTableSortIcons({ sortDirection: SafTableSortDirection.asc }),
                                                                    desc: SafTableSortIcons({ sortDirection: SafTableSortDirection.desc }),
                                                                    false: SafTableSortIcons({ sortDirection: SafTableSortDirection.none }),
                                                                }
                                                                [
                                                                header.column.getIsSorted() as string
                                                                ]
                                                            ) ?? null
                                                        }
                                                    </div>
                                                </Col>
                                            </div>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {renderRows()}
                    </tbody>
                </BTable>
                <ButtonToolbar className="justify-content-between">
                    <DropdownButton
                        title={pageSize}
                        onSelect={(e) => {
                            table.setPageSize(Number(e));
                        }}>
                        <Dropdown.Item eventKey={10}>10</Dropdown.Item>
                        <Dropdown.Item eventKey={25}>25</Dropdown.Item>
                        <Dropdown.Item eventKey={30}>30</Dropdown.Item>
                        <Dropdown.Item eventKey={50}>50</Dropdown.Item>
                    </DropdownButton>
                    <SafTablePaginator
                        totalPages={table.getPageCount()}
                        currentPage={pageIndex}
                        handlePageChange={table.setPageIndex}
                        handleFirst={table.setPageIndex}
                        handlePrevious={table.previousPage}
                        handleNext={table.nextPage}
                        handleLast={table.setPageIndex}
                    />
                </ButtonToolbar>
            </div>


        </Fragment>

    );
};


